import { Link } from "react-router-dom";
import clsx from "clsx";

import { relativeDate } from "../lib/format";
import type { Quarry } from "../lib/types";
import { RarityBadge } from "./RarityBadge";

export type QuarryRowProps = {
  quarry: Quarry;
  className?: string;
};

/**
 * One line of the hunt list.
 *
 * Found quarry are ticked in moss and carry their photograph. Unfound ones give
 * you everything you need to go and look: what it is, where it hides, and when
 * it's about.
 */
export function QuarryRow({ quarry, className }: QuarryRowProps) {
  const found = quarry.isFound;

  return (
    <li
      className={clsx(
        "flex items-start gap-3 border-b border-rule py-4 last:border-b-0",
        quarry.isBonus && "bg-[color-mix(in_oklab,var(--color-ochre)_5%,transparent)] px-3",
        className,
      )}
    >
      <Tick found={found} bonus={quarry.isBonus} label={quarry.name} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h3 className="display-6 text-[1.1875rem] leading-tight text-ink">
            {quarry.foundEntry ? (
              <Link to={`/entry/${quarry.foundEntry.id}`} className="hover:text-ochre">
                {quarry.name}
              </Link>
            ) : (
              quarry.name
            )}
          </h3>

          {quarry.scientificName ? (
            <span className="font-prose text-sm italic text-ink-soft">{quarry.scientificName}</span>
          ) : null}

          {quarry.isBonus ? (
            <span className="field-label-tight text-ochre">Bonus — not on the list</span>
          ) : null}
        </div>

        {found ? (
          <p className="mt-1.5 font-prose text-sm text-moss">
            Found {relativeDate(quarry.foundAt).toLowerCase()}
            {quarry.foundEntry ? (
              <>
                {" · "}
                <Link to={`/entry/${quarry.foundEntry.id}`} className="link">
                  {quarry.foundEntry.displayTitle}
                </Link>
              </>
            ) : null}
          </p>
        ) : (
          <>
            {quarry.blurb ? (
              <p className="mt-1.5 max-w-prose font-prose text-[0.9375rem] leading-relaxed text-ink-soft">
                {quarry.blurb}
              </p>
            ) : null}

            <dl className="mt-2.5 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {quarry.whereToLook ? (
                <div>
                  <dt className="field-label-tight text-ink-soft">Where to look</dt>
                  <dd className="mt-0.5 font-prose text-sm leading-snug text-ink">
                    {quarry.whereToLook}
                  </dd>
                </div>
              ) : null}
              {quarry.seasonality ? (
                <div>
                  <dt className="field-label-tight text-ink-soft">When</dt>
                  <dd className="mt-0.5 font-prose text-sm leading-snug text-ink">
                    {quarry.seasonality}
                  </dd>
                </div>
              ) : null}
            </dl>
          </>
        )}

        <div className="mt-2.5">
          <RarityBadge rarity={quarry.rarity} />
        </div>
      </div>

      {quarry.foundEntry?.thumbUrl ? (
        <Link
          to={`/entry/${quarry.foundEntry.id}`}
          tabIndex={-1}
          className="shrink-0 bg-card p-[3px] shadow-mount"
        >
          <img
            src={quarry.foundEntry.thumbUrl}
            alt={quarry.foundEntry.displayTitle}
            loading="lazy"
            decoding="async"
            className="size-16 object-cover sm:size-20"
          />
        </Link>
      ) : null}
    </li>
  );
}

/** A ticked checklist box, inked by hand. */
function Tick({ found, bonus, label }: { found: boolean; bonus: boolean; label: string }) {
  return (
    <span
      className={clsx(
        "mt-1 flex size-5 shrink-0 items-center justify-center border",
        found ? (bonus ? "border-ochre" : "border-moss") : "border-rule",
      )}
      role="img"
      aria-label={found ? `${label}: found` : `${label}: not yet found`}
    >
      {found ? (
        <svg viewBox="0 0 20 20" aria-hidden="true" className={clsx("size-4", bonus ? "text-ochre" : "text-moss")}>
          {/* A slightly wobbly hand-inked tick. */}
          <path
            d="M3.5 11.2 7.4 15.4 16.6 4.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}
